import { Box, Alert } from "@mui/joy";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { LayoutContext } from "../context";

import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Diversity1RoundedIcon from '@mui/icons-material/Diversity1Rounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import PageHeader from "../components/ui/PageHeader";
import PageContainer from "../components/ui/PageContainer";
import EmptyState from "../components/ui/EmptyState";
import { IconButton } from "@mui/joy";
import { APIGetMyUnions, APIGetUnions, APILikeUnion } from "../api";

/**
 * Союзы.
 *
 * Что поправлено кроме оформления:
 *  - APILikeUnion вызывался, но не был импортирован — лайк союза молча падал;
 *  - вкладка «Мой союз» не переключалась: она сразу уводила на страницу союза,
 *    а если актуального союза нет — показывала alert браузера. Теперь это
 *    нормальная вкладка со списком своих союзов, а переход в актуальный —
 *    отдельной кнопкой в шапке.
 */
export default function UnionsListPage() {
  const { token } = useContext(LayoutContext);
  const navigate = useNavigate();

  const [unions, setUnions] = useState([]);
  const [myUnions, setMyUnions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    loadUnions();
  }, [token]);

  const loadUnions = async () => {
    try {
      setLoading(true);

      const allUnionsResponse = await APIGetUnions(token);
      setUnions(allUnionsResponse.unions || []);

      const myUnionsResponse = await APIGetMyUnions(token);
      setMyUnions(myUnionsResponse.unions || []);
    } catch (err) {
      setError('Ошибка загрузки союзов');
      console.error('Error loading unions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnionClick = (unionId) => {
    navigate(`/union/${unionId}`);
  };

  const handleLikeUnion = async (unionId, isCurrentlyLiked) => {
    try {
      await APILikeUnion(token, unionId, !isCurrentlyLiked);
      loadUnions();
    } catch (err) {
      console.error('Error liking union:', err);
    }
  };

  // Актуальный союз — активный, не заблокированный и не в архиве
  const getActiveUnion = (list) => list.find(union =>
    union.is_active && !union.is_blocked && union.status !== 'archived'
  );

  const activeUnion = getActiveUnion(myUnions);

  const unionTitle = (union) =>
    union.name
    || `Союз ${union.members?.[0]?.profile?.name || ''}${union.members?.[1]?.profile?.name ? ` и ${union.members[1].profile.name}` : ''}`.trim();

  const renderUnionCard = (union, index) => (
    <Box
      key={union.id}
      className="lvc-card lvc-card--hover lvc-in"
      style={{ animationDelay: `${Math.min(index * 0.05, 0.4)}s` }}
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <div className="lvc-union__top">
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <div className="lvc-union__pair">
            {(union.members?.length ? union.members.slice(0, 2) : [null, null]).map((member, i) => (
              member?.profile?.photo_file_path ? (
                <img
                  key={member.id ?? i}
                  className="lvc-union__ava"
                  src={member.profile.photo_file_path}
                  alt={member.profile?.name || ''}
                  loading="lazy"
                />
              ) : (
                <Box
                  key={member?.id ?? `ph-${i}`}
                  className="lvc-union__ava"
                  sx={{ display: 'grid', placeItems: 'center', color: '#b06ad6' }}
                >
                  <PersonRoundedIcon />
                </Box>
              )
            ))}
            <span className="lvc-union__heart">
              <FavoriteRoundedIcon sx={{ fontSize: 13 }} />
            </span>
          </div>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 16.5,
                fontWeight: 800,
                color: 'var(--lvc-ink)',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {unionTitle(union)}
            </Box>
            <Box sx={{ mt: 0.5, fontSize: 12.5, color: 'var(--lvc-ink-soft)' }}>
              {union.members_count || union.members?.length || 0} участника
            </Box>
          </Box>

          <button
            type="button"
            aria-label={union.is_liked ? 'Убрать лайк' : 'Лайкнуть союз'}
            className={`lvc-heart${union.is_liked ? ' is-on' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleLikeUnion(union.id, union.is_liked);
            }}
          >
            {union.is_liked ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
          </button>
        </Box>

        {union.description && (
          <Box
            sx={{
              position: 'relative',
              mt: 1.5,
              fontSize: 13.5,
              lineHeight: 1.45,
              color: 'var(--lvc-ink-soft)',
              display: '-webkit-box',
              WebkitLineClamp: '2',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {union.description}
          </Box>
        )}
      </div>

      <div className="lvc-union__stats">
        <div className="lvc-union__stat">
          <b>{union.posts_count || 0}</b>
          <span>Постов</span>
        </div>
        <div className="lvc-union__stat">
          <b>{union.likes_count || 0}</b>
          <span>Лайков</span>
        </div>
        <div className="lvc-union__stat">
          <b>{union.status_title || '—'}</b>
          <span>Статус</span>
        </div>
      </div>

      <div className="lvc-actions">
        <button
          type="button"
          className="lvc-btn lvc-btn--primary"
          onClick={() => handleUnionClick(union.id)}
        >
          Перейти в союз
          <ArrowOutwardRoundedIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
    </Box>
  );

  const list = activeTab === 'all' ? unions : myUnions;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        title="Союзы"
        subtitle={activeTab === 'all' ? `${unions.length} на сервисе` : `${myUnions.length} у вас`}
        actions={
          <>
            {activeUnion && (
              <button
                type="button"
                className="lvc-btn lvc-btn--soft lvc-btn--sm"
                style={{ flex: 'none' }}
                onClick={() => handleUnionClick(activeUnion.id)}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                Мой союз
              </button>
            )}
            <IconButton
              color="primary"
              variant="soft"
              onClick={loadUnions}
              disabled={loading}
              aria-label="Обновить"
              sx={{ borderRadius: '14px' }}
            >
              <CachedRoundedIcon />
            </IconButton>
          </>
        }
      />

      <PageContainer>
        <Box sx={{ mb: 2.5, display: 'flex' }}>
          <div className="lvc-seg" style={{ width: '100%', maxWidth: 420 }}>
            <button
              type="button"
              className={`lvc-seg__btn${activeTab === 'all' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <GroupsRoundedIcon sx={{ fontSize: 17 }} />
              Все союзы
            </button>
            <button
              type="button"
              className={`lvc-seg__btn${activeTab === 'my' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('my')}
            >
              <Diversity1RoundedIcon sx={{ fontSize: 17 }} />
              Мои союзы
            </button>
          </div>
        </Box>

        {error && (
          <Alert color="danger" variant="soft" sx={{ mb: 2, borderRadius: '16px' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box className="lvc-grid lvc-grid--list">
            {[0, 1, 2, 3].map(i => (
              <Box key={i} className="lvc-skeleton" sx={{ height: 236 }} />
            ))}
          </Box>
        ) : list.length > 0 ? (
          <Box className="lvc-grid lvc-grid--list">
            {list.map(renderUnionCard)}
          </Box>
        ) : activeTab === 'all' ? (
          <EmptyState
            icon={<Diversity1RoundedIcon />}
            title="Союзов пока нет"
            text="Союз — это общая страница пары. Первый появится здесь, как только кто-то его создаст."
          />
        ) : (
          <EmptyState
            icon={<Diversity1RoundedIcon />}
            title="Вы пока не состоите в союзе"
            text="Союз создаётся из взаимной симпатии или прямо из чата — кнопкой «Союз»."
            action={
              <button type="button" className="lvc-btn lvc-btn--primary" onClick={() => navigate('/match')}>
                <FavoriteRoundedIcon sx={{ fontSize: 17 }} />
                К симпатиям
              </button>
            }
          />
        )}
      </PageContainer>
    </Box>
  );
}
